import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Trash2, FileText, RefreshCw, Edit, ChevronLeft, ChevronRight, Eye, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const GreigeSectionEntryManagement = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [operationFilter, setOperationFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const limit = 20;

  // Action dialogs
  const [showMoveToDraftDialog, setShowMoveToDraftDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // View Bundles dialog (Bundle Receipt entries only)
  const [showBundlesDialog, setShowBundlesDialog] = useState(false);
  const [bundlesDialogData, setBundlesDialogData] = useState(null);
  const [bundlesDialogLoading, setBundlesDialogLoading] = useState(false);

  const fetchEntries = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', 100);
      if (operationFilter !== 'all') params.append('operation', operationFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);

      const response = await apiClient.get(`/sections/greige/admin/entries?${params}`);
      let filteredEntries = response.data.entries || [];
      
      // Client-side search filter
      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase();
        filteredEntries = filteredEntries.filter(e =>
          e.entry_id?.toLowerCase().includes(term) ||
          e.bundle_barcode?.toLowerCase().includes(term) ||
          e.document_barcode?.toLowerCase().includes(term) ||
          e.staff_id?.toLowerCase().includes(term)
        );
      }
      
      setTotalEntries(filteredEntries.length);
      setEntries(filteredEntries.slice(page * limit, (page + 1) * limit));
    } catch (error) {
      console.error('Error fetching entries:', error);
      toast.error('Failed to load entries');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, [page, operationFilter, statusFilter]);

  const handleSearch = () => {
    setPage(0);
    fetchEntries();
  };

  const handleMoveToDraft = async () => {
    if (!selectedEntry) return;
    
    setIsProcessing(true);
    try {
      const res = await apiClient.post(`/sections/greige/admin/entries/${selectedEntry.entry_id}/move-to-draft`);
      toast.success(res.data?.message || `Entry ${selectedEntry.entry_id} moved to draft`);
      setShowMoveToDraftDialog(false);
      setSelectedEntry(null);
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to move to draft');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedEntry) return;
    
    setIsProcessing(true);
    try {
      const res = await apiClient.delete(`/sections/greige/admin/entries/${selectedEntry.entry_id}`);
      toast.success(res.data?.message || `Entry ${selectedEntry.entry_id} deleted`);
      setShowDeleteDialog(false);
      setSelectedEntry(null);
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete entry');
    } finally {
      setIsProcessing(false);
    }
  };

  const openBundlesDialog = async (entry) => {
    setShowBundlesDialog(true);
    setBundlesDialogData({ entry });
    setBundlesDialogLoading(true);
    try {
      const r = await apiClient.get(
        `/sections/greige/admin/entries/bundle-receipt/${entry.entry_id}/bundles`
      );
      setBundlesDialogData({ entry, ...r.data });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load bundles');
      setBundlesDialogData({ entry, error: true });
    } finally {
      setBundlesDialogLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-800">Completed</Badge>;
      case 'draft':
        return <Badge className="bg-amber-100 text-amber-800">Draft</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">{status || 'Unknown'}</Badge>;
    }
  };

  const totalPages = Math.ceil(totalEntries / limit);

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/dashboard')} className="text-[#F9FAFB] hover:bg-white/10 mr-3">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold">Greige Section Entry Management</h1>
            <p className="text-sm text-[#E8DCC8]">View, edit, and manage greige section entries</p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Filters */}
        <Card className="mb-6 border-[#E5DFD0]">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Filters & Search</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <Input
                  placeholder="Search by Entry ID, Bundle, or Staff ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>
              <Select value={operationFilter} onValueChange={setOperationFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Operation" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Operations</SelectItem>
                  <SelectItem value="Bundle Receipt Entry">Bundle Receipt Entry</SelectItem>
                  <SelectItem value="Greige Inspection">Greige Inspection</SelectItem>
                  <SelectItem value="Sort No Assignment">Sort No Assignment</SelectItem>
                  <SelectItem value="Bundle Split">Bundle Split</SelectItem>
                  <SelectItem value="Pinning">Pinning</SelectItem>
                  <SelectItem value="Packing">Packing</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end mt-4">
              <Button onClick={handleSearch} className="bg-[#2D5F4F] hover:bg-[#1A1F2E]">
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Entries Table */}
        <Card className="border-[#E5DFD0]">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Entries ({totalEntries})</CardTitle>
              <Button variant="outline" size="sm" onClick={fetchEntries} disabled={isLoading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : entries.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No entries found</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Entry ID</TableHead>
                        <TableHead>Operation</TableHead>
                        <TableHead>Bundle</TableHead>
                        <TableHead>Staff</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {entries.map((entry) => {
                        const isBR = entry.source === 'bundle_receipt';
                        return (
                        <TableRow
                          key={entry.entry_id}
                          data-testid={`entry-row-${entry.entry_id}`}
                          className={isBR ? 'bg-blue-50/30' : ''}
                        >
                          <TableCell className="font-mono text-sm">{entry.entry_id}</TableCell>
                          <TableCell>
                            {entry.operation_name}
                            {isBR && (
                              <Badge className="ml-2 bg-blue-100 text-blue-700 text-[10px]">
                                Receipt
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {isBR
                              ? (entry.bundle_summary || `${entry.received_count || 0} bundles`)
                              : (entry.bundle_barcode || '-')}
                          </TableCell>
                          <TableCell>{entry.staff_id}</TableCell>
                          <TableCell>{getStatusBadge(entry.status)}</TableCell>
                          <TableCell className="text-sm text-gray-500">
                            {entry.timestamp ? new Date(entry.timestamp).toLocaleDateString() : '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              {isBR && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-blue-600 border-blue-300 hover:bg-blue-50"
                                  onClick={() => openBundlesDialog(entry)}
                                  data-testid={`view-bundles-${entry.entry_id}`}
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  Bundles
                                </Button>
                              )}
                              {(entry.status === 'completed' || !entry.status) && entry.status !== 'draft' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-amber-600 border-amber-300 hover:bg-amber-50"
                                  onClick={() => {
                                    setSelectedEntry(entry);
                                    setShowMoveToDraftDialog(true);
                                  }}
                                  data-testid={`to-draft-${entry.entry_id}`}
                                >
                                  <Edit className="w-4 h-4 mr-1" />
                                  To Draft
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 border-red-300 hover:bg-red-50"
                                onClick={() => {
                                  setSelectedEntry(entry);
                                  setShowDeleteDialog(true);
                                }}
                                data-testid={`delete-${entry.entry_id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );})}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <p className="text-sm text-gray-500">
                      Page {page + 1} of {totalPages}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.max(0, p - 1))}
                        disabled={page === 0}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                        disabled={page >= totalPages - 1}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Move to Draft Dialog */}
      <Dialog open={showMoveToDraftDialog} onOpenChange={setShowMoveToDraftDialog}>
        <DialogContent data-testid="move-to-draft-dialog">
          <DialogHeader>
            <DialogTitle>Move Entry to Draft</DialogTitle>
            <DialogDescription>
              This will move the entry back to draft status, allowing users to continue editing it.
              {selectedEntry?.operation_name === 'Greige Inspection' && (
                <span className="block mt-2 text-amber-600">
                  Note: USN inspection records will be reverted and staff work records will be removed.
                </span>
              )}
              {selectedEntry?.source === 'bundle_receipt' && (
                <span className="block mt-2 text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                  <strong>Bundle Receipt:</strong> every received bundle for this consignment will be
                  un-received (removed from <code>greige_bundles</code>) so the consignment can be re-scanned from scratch.
                  Downstream operations are NOT touched — use Delete if you want a full cascade.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm">
              <span className="font-semibold">Entry ID:</span> {selectedEntry?.entry_id}
            </p>
            <p className="text-sm">
              <span className="font-semibold">Operation:</span> {selectedEntry?.operation_name}
            </p>
            <p className="text-sm">
              <span className="font-semibold">
                {selectedEntry?.source === 'bundle_receipt' ? 'Document:' : 'Bundle:'}
              </span>{' '}
              {selectedEntry?.source === 'bundle_receipt'
                ? selectedEntry?.document_barcode
                : selectedEntry?.bundle_barcode}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMoveToDraftDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleMoveToDraft} 
              disabled={isProcessing}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-move-to-draft"
            >
              {isProcessing ? 'Processing...' : 'Move to Draft'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Entry</DialogTitle>
            <DialogDescription>
              This action cannot be undone. The entry and all related records will be permanently deleted.
              {selectedEntry?.source === 'bundle_receipt' && (
                <span className="block mt-2 text-red-700 bg-red-50 border border-red-300 rounded p-2">
                  <strong>⚠ Cascade delete:</strong> this will hard-delete the Bundle Receipt row
                  AND every <code>greige_bundles</code> entry it spawned AND every downstream
                  <code> greige_operations</code> row (Sort No / Inspection / Pinning / Packing) that
                  references those bundles. USN inspection records and staff work logs tied to them
                  are also wiped. Use this only if you intend a full reset of this consignment in Greige.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm">
              <span className="font-semibold">Entry ID:</span> {selectedEntry?.entry_id}
            </p>
            <p className="text-sm">
              <span className="font-semibold">Operation:</span> {selectedEntry?.operation_name}
            </p>
            {selectedEntry?.source === 'bundle_receipt' && (
              <p className="text-sm">
                <span className="font-semibold">Bundles:</span>{' '}
                {selectedEntry.received_count || 0} received /{' '}
                {selectedEntry.expected_count || 0} expected
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleDelete} 
              disabled={isProcessing}
              variant="destructive"
              data-testid="confirm-delete"
            >
              {isProcessing ? 'Deleting...' : 'Delete Entry'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Bundles Dialog — Bundle Receipt entries only */}
      <Dialog open={showBundlesDialog} onOpenChange={setShowBundlesDialog}>
        <DialogContent className="max-w-2xl" data-testid="view-bundles-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-600" />
              Bundles for {bundlesDialogData?.entry?.entry_id}
            </DialogTitle>
            <DialogDescription>
              Document: <code>{bundlesDialogData?.document_barcode || '—'}</code>
              {' · '}Started by: {bundlesDialogData?.started_by_name || '—'}
            </DialogDescription>
          </DialogHeader>
          {bundlesDialogLoading ? (
            <div className="py-8 text-center text-gray-500">
              <RefreshCw className="inline w-5 h-5 animate-spin mr-2" /> Loading…
            </div>
          ) : bundlesDialogData?.error ? (
            <div className="py-6 text-center text-red-600">Failed to load bundles.</div>
          ) : (
            <div className="grid grid-cols-2 gap-4 py-2">
              <div>
                <div className="text-xs font-semibold text-emerald-700 mb-1">
                  Received ({(bundlesDialogData?.received_bundles || []).length})
                </div>
                <div className="border rounded max-h-[40vh] overflow-y-auto bg-emerald-50/30">
                  {(bundlesDialogData?.received_bundles || []).length === 0 ? (
                    <div className="text-xs text-stone-400 p-3 text-center">None</div>
                  ) : (
                    <ul className="text-xs font-mono">
                      {(bundlesDialogData.received_bundles || []).map((b, i) => {
                        // received_bundles entries are objects
                        // {bundle_barcode, scanned_at, scanned_by_name}.
                        const code = typeof b === 'string' ? b : b?.bundle_barcode;
                        const by = typeof b === 'object' ? b?.scanned_by_name : null;
                        return (
                          <li
                            key={`r-${code}-${i}`}
                            className="px-3 py-1 border-b last:border-b-0 border-emerald-100"
                            data-testid={`bundle-received-${code}`}
                          >
                            <span>{code}</span>
                            {by && <span className="ml-2 text-stone-500 text-[10px]">· {by}</span>}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold text-amber-700 mb-1">
                  Pending ({(bundlesDialogData?.pending_bundles || []).length})
                </div>
                <div className="border rounded max-h-[40vh] overflow-y-auto bg-amber-50/30">
                  {(bundlesDialogData?.pending_bundles || []).length === 0 ? (
                    <div className="text-xs text-stone-400 p-3 text-center">None</div>
                  ) : (
                    <ul className="text-xs font-mono">
                      {(bundlesDialogData.pending_bundles || []).map((b, i) => {
                        const code = typeof b === 'string' ? b : b?.bundle_barcode;
                        return (
                          <li
                            key={`p-${code}-${i}`}
                            className="px-3 py-1 border-b last:border-b-0 border-amber-100"
                            data-testid={`bundle-pending-${code}`}
                          >
                            {code}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBundlesDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GreigeSectionEntryManagement;
